import {
  CREATE_CELL,
  CellContentsPayload,
  CellId,
  CellLocation,
  CreateOriginMetadata,
  DereferenceChildCellOptions,
  GLOBAL_CELL_LOCATION,
  HexVersionId,
  MagicEventId,
  StaticCellId,
  StoryElementId,
  createStoryElementPayload,
  uuid,
} from "@hex/common";
import { useCallback } from "react";

import { DispatchAOResult } from "../../../atomic-operations";
import { useStore } from "../../../redux/hooks.js";
import { resolveCellLocation } from "../../../redux/slices/cell-location/index.js";
import { useHexVersionAOContext } from "../../../util/hexVersionAOContext";
import { useProjectContext } from "../../../util/projectContext.js";
import { useSelectCell } from "../useSelectCell";

export interface InsertCellOptions extends DereferenceChildCellOptions {
  /**
   * @default global
   */
  location?: CellLocation;
  /**
   * When passed, used as the value for cellId in the produced CREATE_CELL operation
   */
  cellIdOverride?: CellId;
  runAfterInsertion?: boolean;
  excludeFromHistory?: boolean;
  // use for tracking purposes
  isTemplate?: boolean;
  latestMagicEventId?: MagicEventId;
  origin?: CreateOriginMetadata;
}

export function useInsertCellOp(): (
  contents: CellContentsPayload,
  options?: InsertCellOptions,
) => CREATE_CELL {
  const { hexVersionId } = useProjectContext();
  const insertCellOp = useInsertCellOpWithoutProjectContext();
  return useCallback(
    (contents, options) => {
      return insertCellOp(contents, hexVersionId, options);
    },
    [insertCellOp, hexVersionId],
  );
}

export function useInsertCellOpWithoutProjectContext(): (
  contents: CellContentsPayload,
  hexVersionId: HexVersionId,
  options?: InsertCellOptions,
) => CREATE_CELL {
  const store = useStore();

  return useCallback(
    (
      contents,
      hexVersionId,
      {
        cellIdOverride: newCellId = uuid() as CellId,
        excludeFromHistory,
        isTemplate,
        latestMagicEventId,
        location = GLOBAL_CELL_LOCATION,
        origin,
        runAfterInsertion,
        ...derefOptions
      } = {},
    ) => {
      const {
        order: insertAt,
        parentBlockCellId,
        parentCellId,
        parentComponentImportCellId,
      } = resolveCellLocation({
        state: store.getState(),
        hexVersionId,
        location,
        ...derefOptions,
      });

      return CREATE_CELL.create({
        cellId: newCellId,
        staticCellId: uuid() as StaticCellId,
        insertAfter: undefined,
        insertBefore: undefined,
        insertAt,
        contents,
        storyElement: createStoryElementPayload({
          id: uuid() as StoryElementId,
        }),
        runAfterInsertion,
        componentImportCellId:
          contents.type === "COMPONENT_IMPORT" ? contents.id : null,
        parentBlockCellId: parentBlockCellId ?? undefined,
        parentComponentImportCellId: parentComponentImportCellId ?? undefined,
        parentCellId: parentCellId ?? undefined,
        blockCellId: contents.type === "BLOCK" ? contents.id : null,
        excludeFromHistory,
        isTemplate,
        latestMagicEventId,
        origin,
      });
    },
    [store],
  );
}

export function useInsertCell(): (
  contents: CellContentsPayload,
  options?: InsertCellOptions,
) => { cellId: CellId; dispatchResult: DispatchAOResult } {
  const insertCellOp = useInsertCellOp();

  const { dispatchAO } = useHexVersionAOContext();
  const { selectCells } = useSelectCell();

  return useCallback(
    (contents, options) => {
      const operation = insertCellOp(contents, options);
      const dispatchResult = dispatchAO(operation);

      // not sure we should really be doing this here,
      // but going to leave it for back compat
      if (options?.location == null || options.location.type === "global") {
        // TODO(VELO-5389): fix this scrolling behavior
        selectCells(operation.payload.cellId);
      }

      return { cellId: operation.payload.cellId, dispatchResult };
    },
    [dispatchAO, insertCellOp, selectCells],
  );
}
